import { useMemo } from 'react';
import { useListAutomationTriggers } from '@/generated/hooks/grimoire/v1/automation_phase';
import { useGetAutomationSchedule } from '@/generated/hooks/grimoire/v1/api';
import {
  useRetrieveWorkspacePollInstances,
  useRetrieveWorkspaceTriggerInstances,
} from '@/generated/hooks/bdk/v2/services/library';
import {
  useUserPreferencesStore,
  selectCurrentOrganizationId,
  selectCurrentWorkspaceId,
} from '@/shared/stores';
import { FilterBuilder, isNotFoundError } from '@/shared/utils';
import type { V1AutomationTrigger } from '@/generated/hooks/grimoire/models';
import {
  V2PollInstanceState,
  V2TriggerInstanceState,
  type V2PollInstance,
  type V2TriggerInstance,
} from '@/generated/hooks/bdk/models';

// Polling interval in milliseconds (5 seconds) for non-terminal trigger states
const POLLING_INTERVAL = 5000;

// Maximum number of polling retries before stopping
const MAX_POLLING_RETRIES = 25;

/**
 * Matched trigger type combining Grimoire trigger info and BDK trigger instance.
 */
export type TMatchedTrigger = {
  grimoireTrigger: V1AutomationTrigger;
  bdkInstance: V2TriggerInstance | V2PollInstance;
};

/**
 * Options for the useAutomationTriggers hook.
 */
interface IUseAutomationTriggersOptions {
  /** The automation ID to fetch triggers for */
  automationId: string;
  /** Whether to fetch DRAFT or PUBLISHED triggers */
  isDraft: boolean;
  /** Whether the queries should be enabled (defaults to true) */
  enabled?: boolean;
}

/**
 * Hook to fetch and match automation triggers from Grimoire and BDK.
 *
 * Fetches triggers from Grimoire filtered by stage (DRAFT/PUBLISHED) and matches
 * them with trigger instances from BDK based on trigger_id.
 *
 * @param {IUseAutomationTriggersOptions} options - Hook options
 * @returns Object containing matched triggers, schedule status, and loading state
 */
export function useAutomationTriggers({
  automationId,
  isDraft,
  enabled = true,
}: IUseAutomationTriggersOptions): {
  matchedTriggers: TMatchedTrigger[];
  hasSchedule: boolean;
  isScheduleEnabled: boolean;
  scheduleSummary?: string;
  isLoading: boolean;
} {
  // Get organization and workspace IDs for API calls
  const organizationId = useUserPreferencesStore(selectCurrentOrganizationId);
  const workspaceId = useUserPreferencesStore(selectCurrentWorkspaceId);

  // Build stage filter for Grimoire based on isDraft
  const grimoireStageFilter = FilterBuilder.create()
    .equals('stage', isDraft ? 'DRAFT' : 'PUBLISHED')
    .build();

  const bdkTriggerInstancesFilter = FilterBuilder.create()
    .has('automation_ids', automationId)
    .build();

  const isQueryEnabled =
    enabled && Boolean(organizationId && workspaceId && automationId);

  // Fetch triggers from Grimoire with stage filter
  const { data: grimoireTriggersData, isLoading: isLoadingGrimoire } =
    useListAutomationTriggers(
      organizationId,
      workspaceId,
      automationId,
      { filter: grimoireStageFilter },
      {
        query: {
          enabled: isQueryEnabled,
        },
      },
    );

  // Fetch trigger instances from BDK
  const { data: bdkTriggerInstancesData, isLoading: isLoadingTriggerBdk } =
    useRetrieveWorkspaceTriggerInstances(
      organizationId,
      workspaceId,
      { filter: bdkTriggerInstancesFilter },
      {
        query: {
          enabled: isQueryEnabled,
          refetchInterval: query => {
            const instances = query.state.data?.data?.trigger_instances ?? [];

            // Keep polling if any trigger is not in a terminal state (READY or FAILED)
            const hasNonTerminalTrigger = instances.some(
              (instance: V2TriggerInstance) =>
                instance.status?.state !==
                  V2TriggerInstanceState.TRIGGER_INSTANCE_STATE_READY &&
                instance.status?.state !==
                  V2TriggerInstanceState.TRIGGER_INSTANCE_STATE_FAILED,
            );

            // Stop polling if max retries reached
            if (query.state.dataUpdateCount >= MAX_POLLING_RETRIES) {
              return false;
            }

            return hasNonTerminalTrigger ? POLLING_INTERVAL : false;
          },
        },
      },
    );

  // Fetch poll trigger instances from BDK
  const { data: bdkPollInstancesData, isLoading: isLoadingPollBdk } =
    useRetrieveWorkspacePollInstances(
      organizationId,
      workspaceId,
      { filter: bdkTriggerInstancesFilter },
      {
        query: {
          enabled: isQueryEnabled,
          refetchInterval: query => {
            const instances = query.state.data?.data?.poll_instances ?? [];

            // Keep polling if any trigger is not in a terminal state (READY or FAILED)
            const hasNonTerminalTrigger = instances.some(
              (instance: V2PollInstance) =>
                instance.status?.state !==
                  V2PollInstanceState.POLL_INSTANCE_STATE_READY &&
                instance.status?.state !==
                  V2PollInstanceState.POLL_INSTANCE_STATE_FAILED,
            );

            // Stop polling if max retries reached
            if (query.state.dataUpdateCount >= MAX_POLLING_RETRIES) {
              return false;
            }

            return hasNonTerminalTrigger ? POLLING_INTERVAL : false;
          },
        },
      },
    );

  // Match Grimoire triggers with BDK instances
  // Grimoire trigger.trigger_id === BDK instance.id
  const matchedTriggers = useMemo(() => {
    const grimoireTriggers =
      grimoireTriggersData?.data?.automation_triggers ?? [];
    const bdkTriggerInstances =
      bdkTriggerInstancesData?.data?.trigger_instances ?? [];
    const bdkPollInstances = bdkPollInstancesData?.data?.poll_instances ?? [];

    return grimoireTriggers
      .map(grimoireTrigger => {
        const bdkTriggerInstance = bdkTriggerInstances.find(
          instance => instance.id === grimoireTrigger.trigger_id,
        );
        const bdkPollInstance = bdkPollInstances.find(
          instance => instance.id === grimoireTrigger.trigger_id,
        );
        return bdkTriggerInstance
          ? { grimoireTrigger, bdkInstance: bdkTriggerInstance }
          : bdkPollInstance
            ? { grimoireTrigger, bdkInstance: bdkPollInstance }
            : null;
      })
      .filter(item => item !== null);
  }, [
    grimoireTriggersData?.data?.automation_triggers,
    bdkTriggerInstancesData?.data?.trigger_instances,
    bdkPollInstancesData?.data?.poll_instances,
  ]);

  // Fetch schedule to check if automation has a schedule (active or paused)
  // Schedules are stored separately from V1AutomationTrigger records
  const {
    data: scheduleData,
    error: scheduleError,
    isLoading: isLoadingSchedule,
    isFetched: isScheduleFetched,
  } = useGetAutomationSchedule(organizationId, workspaceId, automationId, {
    query: {
      enabled: isQueryEnabled,
      retry: false,
    },
  });

  // hasSchedule is true if a schedule exists (regardless of enabled/paused state)
  // After a delete, the GET 404s but React Query retains the stale data, so we
  // also check the error to flip hasSchedule immediately.
  const hasSchedule = !!scheduleData?.data && !isNotFoundError(scheduleError);
  // isScheduleEnabled is true if the schedule exists and is not paused
  const isScheduleEnabled =
    hasSchedule && scheduleData?.data?.enabled !== false;
  const scheduleSummary = hasSchedule ? scheduleData?.data?.summary : undefined;

  // An automation without a schedule 404s, so this query never caches data.
  // React Query resets a query to `pending` on every refetch while its data is
  // undefined, which would flip isLoading back to true long after the first
  // answer — enough to unmount consumers mid-interaction (e.g. opening the
  // schedule dialog re-enables the same query). Once the query has settled once
  // we know whether a schedule exists, so only the first load counts as loading.
  const isLoading =
    isLoadingGrimoire ||
    isLoadingTriggerBdk ||
    isLoadingPollBdk ||
    (isLoadingSchedule && !isScheduleFetched);

  return {
    matchedTriggers,
    hasSchedule,
    isScheduleEnabled,
    scheduleSummary,
    isLoading,
  };
}
